import { useQuery } from '@apollo/client'
import get from 'lodash/get'
import PropTypes from 'prop-types'

import getDisplayName from 'utils/getDisplayName'
import { getTotalPages } from 'utils/pagination'
import { relayConnectionToArray } from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'
import {
  getColorVariantSwatchesInfo,
  getProductCode,
  getProductFabrication,
  getProductImages,
  getProductName,
} from 'shop/products/getters'
import { PRODUCT_COLOR_CODE } from 'shop/products/traits'
import { transformCollectionProducts } from 'shop/products/transformations'

import {
  collectionProductsQuery,
  collectionProductsTotalQuery,
} from './AddMoreProductsModal.queries'
import { getUnavailableVariantIds } from './AddMoreProductsModal.transformations'
import { calculateWholesaleRange } from 'containers/ProductCatalog/ProductCatalog.transformations'

const PAGE_SIZE = 15

const withProductsFormatter = (WrappedComponent) => {
  const Wrapper = (props) => {
    const {
      searchText,
      brandId,
      selectedCollectionId,
      loading,
      orderCommonInfo,
      loadingOrderCommonInfo,
      priceTypeId,
    } = props

    const queryVariables = {
      filter: {
        collectionIds: [selectedCollectionId],
        ...(searchText && { searchText }),
      },
      brandIds: [brandId],
      priceTypeId,
    }
    const {
      loading: collectionProductQueryLoading,
      data,
      fetchMore,
    } = useQuery(collectionProductsQuery, {
      variables: {
        ...queryVariables,
        first: PAGE_SIZE,
        after: '',
      },
      client: atlasClient,
      skip: !orderCommonInfo || !selectedCollectionId,
      notifyOnNetworkStatusChange: true,
    })

    const collectionProductTotalQueryVariables = {
      filter: queryVariables.filter,
      brandIds: queryVariables.brandIds,
      priceTypeId: queryVariables.priceTypeId,
    }
    const { loading: collectionProductTotalQueryLoading, data: totalData } =
      useQuery(collectionProductsTotalQuery, {
        variables: collectionProductTotalQueryVariables,
        client: atlasClient,
        skip:
          !orderCommonInfo ||
          !selectedCollectionId ||
          !queryVariables.brandIds.length,
      })

    const products = transformCollectionProducts(
      relayConnectionToArray(get(data, 'collectionProducts')),
    )
    const productsFormatted = products.map((product) => ({
      id: product.id,
      name: getProductName(product),
      code: getProductCode(product),
      wholesaleRange: calculateWholesaleRange(product),
      retailRange: {
        min: get(product, 'priceRange.minRetailValue', null),
        max: get(product, 'priceRange.maxRetailValue', null),
      },
      images: getProductImages(product).map((image) => image.url),
      badges: product.badges,
      fabrication: getProductFabrication(product),
      swatchesInfo: getColorVariantSwatchesInfo({
        product,
      }),
    }))

    const totalItems = get(totalData, 'collectionProductsTotal', 0)
    const customProps = {
      products: productsFormatted,
      unavailableVariantIds: getUnavailableVariantIds(
        products,
        PRODUCT_COLOR_CODE,
      ),
      loading:
        loadingOrderCommonInfo ||
        loading ||
        collectionProductQueryLoading ||
        collectionProductTotalQueryLoading,
      pageSize: PAGE_SIZE,
      totalPages: getTotalPages(totalItems, PAGE_SIZE),
      hasNextPage: get(data, 'collectionProducts.pageInfo.hasNextPage', false),
      loadMoreEntries: (pageSize) =>
        fetchMore({
          query: collectionProductsQuery,
          client: atlasClient,
          variables: {
            ...queryVariables,
            after: get(data, 'collectionProducts.pageInfo.endCursor'),
            first: pageSize,
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = get(fetchMoreResult, 'collectionProducts.edges')
            return newEdges.length
              ? {
                  collectionProducts: {
                    ...get(previousResult, 'collectionProducts'),
                    edges: [
                      ...get(previousResult, 'collectionProducts.edges', []),
                      ...newEdges,
                    ],
                    pageInfo: get(
                      fetchMoreResult,
                      'collectionProducts.pageInfo',
                    ),
                  },
                }
              : previousResult
          },
        }),
    }

    return <WrappedComponent {...props} {...customProps} />
  }

  Wrapper.displayName = `withProductsFormatter(${getDisplayName(
    WrappedComponent,
  )})`
  Wrapper.propTypes = {
    loading: PropTypes.bool,
    orderCommonInfo: PropTypes.object.isRequired,
    brandId: PropTypes.string.isRequired,
    priceTypeId: PropTypes.string.isRequired,
  }
  Wrapper.defaultProps = {
    loading: false,
  }

  return Wrapper
}

export default withProductsFormatter
